import React, { useEffect, useState } from "react";
import { Typography } from "@material-ui/core";
import styles from "./Details.module.scss";
import { Card } from "common/components/Card";
import {
  changeMeter,
  changePeriod,
  changeInterval,
  updateDataset,
  getDataType,
  getDatasetInitialValue,
} from "nodeDetails/functions/DetailFunctions";
import PeriodSelector from "./PeriodSelector";
import moment from "moment";
import { SelectMenu } from "common/components/SelectMenu";
import { MaxSizedCard } from "common/components/MaxSizedCard";
import { Chart } from "../chart/Chart";
import {
  getBarChartOptions,
  getLineChartOptions,
  setSuggestedMaxForConsumption,
  setSuggestedMaxForIndoorClimate,
  getBarData,
  getLineData,
  getTitle,
} from "nodeDetails/functions/chartFunctions";
import { createSelector } from "reselect";
import { IApplicationState } from "redux/root-reducer";
import { useDispatch, useSelector } from "react-redux";
import {
  IDateHandlerObject,
  PeriodType,
  UtlType,
  ISelectedDatasets,
} from "../../types";
import { t } from "i18next";
import { ChartData, ChartOptions } from "chart.js";
import { dataInterval, DeviceType } from "common/types";
import {
  getMaxTemp,
  getMinTemp,
  getDisplayNameOnDeviceType,
  isInstallationSensor,
  groupInstallations,
  capitalizeFirstLetter,
} from "../../functions/dataFunctions";
import {
  getInstallations,
  setSelectedInstallation,
} from "nodeDetails/nodeDetails.actions";
import { useParams } from "react-router-dom";
import {
  getAvailablePeriods as getAvailablePeriods,
  getCurrentMonthUnixTimeObject,
} from "../../functions/dateFunctions";
import { isLandlordTypeGuard } from "common/models/userInfo";
import { KeyValues } from "./KeyValues";
import { actionCreator } from "nodeDetails/nodeDetails.reducer";

const getDetailsState = createSelector(
  (state: IApplicationState) => state,
  (globalState) => ({
    currentTemperature: globalState.node.currentTemperatureForNode,
    displayName: globalState.node.selectedInstallation?.displayName,
    measuringPoints: globalState.node.selectedInstallation?.measuringPoints,
    isFetching: globalState.node.isFetching,
    currency: globalState.common.currentUserInfo.DomainSettings.Currency,
    occupationDate: isLandlordTypeGuard(globalState.common.currentUserInfo)
      ? undefined
      : globalState.common.currentUserInfo.OccupationDate,
    availableInstallations: globalState.node.installations
      ? groupInstallations(
          globalState.node.installations.filter(
            (installation) => !isInstallationSensor(installation)
          )
        ).map((installation) =>
          getDisplayNameOnDeviceType(installation.DeviceType)
        )
      : undefined,
    userIsReady: globalState.common.userIsReady,
    canViewHours:
      globalState.common.currentUserInfo.DomainSettings.TenantAccessStatus
        .canViewHours,
    selectedNode: globalState.node.selectedNode,
    installations: globalState.node.installations,
    max: getMaxTemp(
      globalState.node.selectedInstallation?.detailedInstallation.data || []
    ),
    min: getMinTemp(
      globalState.node.selectedInstallation?.detailedInstallation.data || []
    ),
    installationError: globalState.node.error,
  })
);
const initDate = (): IDateHandlerObject => {
  let date = moment();
  if (date.date() === 1) {
    date = date.subtract(1, "month");
  }
  const from = moment.utc(date.startOf("month"));
  const to = moment.utc(date.endOf("month"));
  const obj = {
    period: "Month",
    from: from,
    to: to,
  };
  return obj;
};
export const Details = () => {
  const {
    installations,
    availableInstallations,
    max,
    min,
    selectedNode,
    currency,
    displayName,
    measuringPoints,
    canViewHours,
    isFetching,
    occupationDate,
    userIsReady,
    installationError,
  } = useSelector(getDetailsState);
  const { data, previousData } =
    useSelector(
      (store: IApplicationState) =>
        store.node.selectedInstallation?.detailedInstallation
    ) || {};
  const initDate = (): IDateHandlerObject => {
    let now = moment();
    let from = moment().startOf("month");
    let to = moment().endOf("month");

    if (now.date() === 1) {
      from = from.subtract(1, "month");
      to = to.subtract(1, "month");
    }
    const obj = {
      period: "Month",
      from: from,
      to: to,
    };
    return obj;
  };

  const { includeVAT } = useSelector(
    (state: IApplicationState) => state.common
  );

  const { domain, id }: any = useParams();
  const dispatch = useDispatch();
  const [availableDatasets, setAvailableDatasets] = useState([""]);
  const [selectedDataType, setSelectedDataType] = useState(0);
  const [selectedDataset, setSelectedDataset] = useState<ISelectedDatasets>();
  const [selectedInterval, setSelectedInterval] = useState(dataInterval.Day);
  const [selectedMeasuringPoint, setSelectedMeasuringPoint] = useState<
    string | undefined
  >(undefined);
  const [isIndoorClimate, setIsIndoorClimate] = useState<boolean>();
  const [utlType, setUtlType] = useState<UtlType>();
  const unitless =
    utlType === UtlType.HeatCostAllocator ||
    utlType === UtlType.HeatCostAllocatorE;
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>("month");
  const [chartData, setChartData] = useState<ChartData>();
  const [chartOptions, setChartOptions] = useState<ChartOptions>();
  const [selectedInstallationName, setSelectedInstallationName] =
    useState(displayName);
  const [availableMeasuringPoints, setAvailableMeasuringPoints] = useState([
    t("Total consumption"),
  ]);
  const [dateHandler, setDateHandler] = useState<IDateHandlerObject>(
    initDate()
  );

  useEffect(() => {
    if (!displayName && !isFetching && userIsReady && !installationError) {
      if (!installations && !installationError)
        dispatch(getInstallations(domain, id));
      const firstDeviceType = installations?.find(
        (i) => !isInstallationSensor(i) && i.DeviceType
      );
      if (!firstDeviceType) return;
      const currentMonthUnixTimeObject = getCurrentMonthUnixTimeObject();
      dispatch(
        setSelectedInstallation(
          domain,
          id,
          firstDeviceType.DeviceType,
          currentMonthUnixTimeObject,
          dataInterval.Day
        )
      );
    }
  }, [isFetching, installations, userIsReady]);

  useEffect(() => {
    if (displayName) {
      setSelectedInstallationName(displayName);
    }
    if (data && data.length) {
      setUtlType(data[0].Utl);

      if (
        data[0].Utl === UtlType.RelativeHumidity ||
        data[0].Utl === UtlType.Temperature
      ) {
        setIsIndoorClimate(true);
        const relativeHumidityVisible = data.length > 1;
        setChartData(getLineData(selectedPeriod, data)),
          setChartOptions(
            getLineChartOptions(
              setSuggestedMaxForIndoorClimate(data),
              relativeHumidityVisible,
              getTitle(data)
            )
          );
      } else {
        setIsIndoorClimate(false);
        if (!selectedDataset || selectedDataset.currentData === undefined) {
          let unit = unitless
            ? ""
            : data[0].Func === "price" && currency
            ? currency
            : data[0].Unit;
          setChartData(
            getBarData(
              selectedPeriod,
              data[0].Values,
              previousData?.length ? previousData[0].Values : []
            )
          );
          setChartOptions(
            getBarChartOptions(
              getTitle(data),
              setSuggestedMaxForConsumption([
                data[0],
                previousData?.length ? previousData[0] : data[0],
              ]),
              unit
            )
          );
        } else {
          let unit = unitless
            ? ""
            : selectedDataset.currentData.Func === "price"
            ? currency
              ? currency
              : selectedDataset.currentData.Unit
            : selectedDataset.currentData.Unit;

          setChartOptions(
            getBarChartOptions(
              getTitle(data),
              setSuggestedMaxForConsumption([
                selectedDataset.currentData,
                selectedDataset.previousData
                  ? selectedDataset.previousData
                  : data[0],
              ]),
              unit
            )
          );
          setChartData(
            getBarData(
              selectedPeriod,
              selectedDataset.currentData.Values,
              selectedDataset.previousData
                ? selectedDataset.previousData.Values
                : []
            )
          );
        }
      }
    }
  }, [displayName, currency, data, selectedDataset, previousData, utlType]);

  useEffect(() => {
    if (data && data.length) {
      if (
        data[0].Utl !== UtlType.RelativeHumidity &&
        data[0].Utl !== UtlType.Temperature
      ) {
        if (currency) {
          let datasets = [currency];

          data.forEach((d) => {
            if (!d) return;

            if (d.Func === "con") {
              if (
                [
                  UtlType.HeatCostAllocator,
                  UtlType.HeatCostAllocatorE,
                ].includes(d.Utl)
              ) {
                datasets.push(t("Units"));
              } else {
                datasets.push(capitalizeFirstLetter(d.Unit));
              }
            }
            if (d.Func === "co2") {
              datasets.push("CO2e");
            }
          });
          setAvailableDatasets(datasets);
        }
      }

      if (selectedDataset) {
        if (
          selectedDataset.currentData === undefined ||
          selectedDataset.currentData.Func === "avg"
        ) {
          updateSelectedDataset("con");
          return;
        }
        updateSelectedDataset(
          selectedDataset ? selectedDataset.currentData.Func : "con"
        );
        setSelectedDataType(
          getDataType(
            selectedDataset ? selectedDataset.currentData.Func : "con"
          )
        );
      }
    }
  }, [data]);

  useEffect(() => {
    selectedInstallationName === getDisplayNameOnDeviceType(DeviceType.Climate)
      ? setIsIndoorClimate(true)
      : setIsIndoorClimate(false);
  }, [selectedInstallationName, includeVAT]);

  const handlePeriodChange = (dateHandlerObj: IDateHandlerObject) => {
    setDateHandler(dateHandlerObj);
    const measuringPointId = measuringPoints?.find(
      (m) => m.Name === selectedMeasuringPoint
    )?.ID;
    const period = changePeriod(dateHandlerObj);
    dispatch(
      setSelectedInstallation(
        domain || "",
        id || 0,
        installations?.find(
          (installation) =>
            getDisplayNameOnDeviceType(installation.DeviceType) ===
            selectedInstallationName
        )?.DeviceType || 0,
        period,
        selectedInterval,
        1,
        measuringPointId
      )
    );
  };

  const handleIntervalChange = (newPeriod: PeriodType) => {
    const { from, to, interval, periodType, unixTimeObject } =
      changeInterval(newPeriod);
    setSelectedInterval(interval);
    setSelectedPeriod(periodType);
    setDateHandler({
      period: newPeriod,
      from: moment.unix(from),
      to: moment.unix(to),
    });

    dispatch(
      setSelectedInstallation(
        domain || "",
        id || 0,
        installations?.find(
          (installation) =>
            getDisplayNameOnDeviceType(installation.DeviceType) ===
            selectedInstallationName
        )?.DeviceType || 0,
        unixTimeObject,
        interval
      )
    );
  };

  const handleMeterChange = (meter: string) => {
    const period = changeMeter(selectedPeriod, dateHandler);
    setSelectedInstallationName(meter);
    dispatch(
      setSelectedInstallation(
        domain || "",
        id || 0,
        installations?.find(
          (installation) =>
            getDisplayNameOnDeviceType(installation.DeviceType) === meter
        )?.DeviceType || 0,
        period,
        selectedInterval
      )
    );
  };

  const updateSelectedDataset = (dataset: string) => {
    if (data) {
      if (previousData) {
        const { currentData, prevData } = updateDataset(
          dataset,
          data,
          currency || "",
          previousData
        );
        setSelectedDataset({
          currentData: currentData,
          previousData: prevData,
        });
        if (!currentData) return;
        setSelectedDataType(getDataType(currentData.Func));
      } else {
        const { currentData, prevData } = updateDataset(
          dataset,
          data,
          currency || ""
        );
        setSelectedDataset({
          currentData: currentData,
          previousData: prevData,
        });
        if (!currentData) return;
        setSelectedDataType(getDataType(currentData.Func));
      }
    }
  };

  useEffect(() => {
    const setMeasuringPointsArray = [t("Total consumption")];
    if (measuringPoints && measuringPoints?.length > 1) {
      measuringPoints?.forEach((m) => {
        setMeasuringPointsArray?.push(m.Name);
      });
    }
    setAvailableMeasuringPoints(setMeasuringPointsArray);
  }, [measuringPoints]);

  const handleMeasuringPointChange = (name: string) => {
    if (!measuringPoints) return;
    setSelectedMeasuringPoint(name);
    const measuringPointId = measuringPoints.find((m) => m.Name === name)?.ID;
    const unixTimeObject = changeMeter(selectedPeriod, dateHandler);
    dispatch(
      setSelectedInstallation(
        domain || "",
        id || 0,
        installations?.find(
          (installation) =>
            getDisplayNameOnDeviceType(installation.DeviceType) ===
            selectedInstallationName
        )?.DeviceType || 0,
        unixTimeObject,
        selectedInterval,
        1,
        measuringPointId
      )
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.headlineContainer}>
        <Typography variant="h5">{selectedInstallationName}</Typography>
      </div>
      <div className={styles.dropdownContainer}>
        <SelectMenu
          menuItems={getAvailablePeriods(canViewHours)}
          onChange={(e) => handleIntervalChange(e as PeriodType)}
          initialValue={t("Month") || ""}
          spacing={3}
        />
        <SelectMenu
          menuItems={availableInstallations || []}
          onChange={handleMeterChange}
          initialValue={selectedInstallationName || ""}
          spacing={3}
        />
        <SelectMenu
          menuItems={availableDatasets}
          onChange={updateSelectedDataset}
          initialValue={getDatasetInitialValue(
            data,
            availableDatasets,
            selectedDataType,
            currency
          )}
          spacing={3}
          disabled={isIndoorClimate}
        />
      </div>
      <MaxSizedCard>
        {installationError && (
          <span>
            {t("Something went wrong. Reload the page and try again")}
          </span>
        )}
        {chartOptions && (
          <Chart
            type={isIndoorClimate ? "line" : "bar"}
            data={data && data.length ? chartData : []}
            options={chartOptions}
            comparisonYear={
              !isIndoorClimate
                ? `${t("Comparison year")}: ${dateHandler.to.year() - 1}`
                : ""
            }
          />
        )}
      </MaxSizedCard>
      {!isIndoorClimate && availableMeasuringPoints.length > 1 && (
        <SelectMenu
          onChange={handleMeasuringPointChange}
          initialValue={t("Total consumption")}
          menuItems={availableMeasuringPoints}
        />
      )}

      <Card>
        <KeyValues utlType={utlType} isIndoorClimate={isIndoorClimate} />
      </Card>

      <div className={styles.fillerContainer}></div>
      <div className={styles.periodContainer}>
        <PeriodSelector
          onChange={handlePeriodChange}
          dataHandlerObject={dateHandler}
          occupationDate={occupationDate}
        ></PeriodSelector>
      </div>
    </div>
  );
};
